'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const matterTypes = [
  'Regulatory Examination (OCC / FDIC / Federal Reserve)',
  'M&A Transaction & Merger Opinion',
  'Fintech Licensing (Single or Multi-State)',
  'Compliance Program Audit',
  'BSA/AML Program Design',
  'Other Banking Regulatory Matter',
];

const institutionSizes = [
  'Under $500M in assets',
  '$500M – $2B in assets',
  '$2B – $10B in assets',
  '$10B – $50B in assets',
  'Over $50B / Systemically Important',
  'Non-bank / Fintech / PE Sponsor',
];

const callbackWindows = [
  'Within 24 hours (urgent matter)',
  'This week (standard)',
  'Next week (planning)',
  'I prefer email communication only',
];

export default function ConsultationFormSection() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    matterType: '',
    institutionSize: '',
    state: '',
    callbackWindow: '',
    email: '',
    name: '',
    message: '',
  });

  const updateForm = (key: string, value: string) => setForm({ ...form, [key]: value });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Backend integration point: POST to /api/consultations with form data
    setSubmitted(true);
  };

  return (
    <section
      id="consultation-form"
      className="py-24 px-6 noise-overlay relative"
      style={{ background: 'var(--navy)' }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #B8943E, transparent)' }}
      />

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-[10px] font-semibold tracking-widest3 uppercase text-gold/60 block mb-3">
            Engage Statute
          </span>
          <h2
            className="font-serif font-light text-marble"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.02em' }}
          >
            Request a Confidential Consultation
          </h2>
          <p className="text-[12px] text-marble/40 mt-4 max-w-lg mx-auto leading-relaxed">
            Every inquiry is reviewed by a partner within two hours during business days. All communications are protected by attorney-client privilege from first contact.
          </p>
        </div>

        {/* Step indicators */}
        {!submitted && (
          <div className="flex items-center justify-center gap-2 mb-10">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => s < step && setStep(s)}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all"
                    style={{
                      background: step >= s ? '#B8943E' : 'rgba(255,255,255,0.08)',
                      color: step >= s ? '#0B1D3A' : 'rgba(255,255,255,0.3)',
                      border: step === s ? '2px solid #B8943E' : '1px solid rgba(184,148,62,0.2)',
                    }}
                  >
                    {step > s ? <Icon name="CheckIcon" variant="outline" size={13} className="text-navy" /> : s}
                  </div>
                  <span className="text-[10px] tracking-widest uppercase text-marble/40 hidden sm:block">
                    {s === 1 ? 'Matter Type' : s === 2 ? 'Institution' : 'Contact'}
                  </span>
                </div>
                {s < 3 && (
                  <div
                    className="w-12 h-px"
                    style={{ background: step > s ? '#B8943E' : 'rgba(184,148,62,0.2)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Form card */}
        <div
          className="rounded-sm p-8 md:p-12"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(184,148,62,0.2)',
          }}
        >
          {submitted ? (
            <div className="text-center py-10">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: 'rgba(184,148,62,0.12)', border: '1px solid rgba(184,148,62,0.3)' }}
              >
                <Icon name="CheckBadgeIcon" variant="outline" size={28} className="text-gold" />
              </div>
              <h3 className="font-serif text-marble text-2xl mb-3">Inquiry Received</h3>
              <p className="text-[13px] text-marble/50 max-w-md mx-auto leading-relaxed">
                A Statute partner will review your matter and respond within two hours. If this is an urgent regulatory matter requiring immediate attention, please call{' '}
                <span className="text-gold font-medium">+1 (202) 847-3300</span>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Step 1 */}
              {step === 1 && (
                <div>
                  <h3 className="font-serif text-marble text-xl mb-2">What is the nature of your matter?</h3>
                  <p className="text-[12px] text-marble/40 mb-6">
                    Select the category that best describes your legal need.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {matterTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => updateForm('matterType', type)}
                        className="text-left p-4 rounded-sm text-[12px] transition-all"
                        style={{
                          background:
                            form.matterType === type
                              ? 'rgba(184,148,62,0.15)'
                              : 'rgba(255,255,255,0.04)',
                          border:
                            form.matterType === type
                              ? '1px solid #B8943E' :'1px solid rgba(184,148,62,0.15)',
                          color: form.matterType === type ? '#D4AF6A' : 'rgba(244,243,239,0.55)',
                        }}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => form.matterType && setStep(2)}
                    disabled={!form.matterType}
                    className="mt-8 btn-red px-8 py-3.5 text-[11px] font-semibold tracking-widest uppercase rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Continue
                  </button>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div>
                  <h3 className="font-serif text-marble text-xl mb-2">Tell us about your institution.</h3>
                  <p className="text-[12px] text-marble/40 mb-6">
                    This helps us assign the right partner and prepare for your consultation.
                  </p>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-[10px] font-semibold tracking-widest uppercase text-marble/40 mb-2">
                        Institution Size
                      </label>
                      <select
                        value={form.institutionSize}
                        onChange={(e) => updateForm('institutionSize', e.target.value)}
                        className="form-field w-full px-4 py-3.5 text-[13px] rounded-sm"
                      >
                        <option value="">Select size...</option>
                        {institutionSizes.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold tracking-widest uppercase text-marble/40 mb-2">
                        Primary State of Operation
                      </label>
                      <input
                        type="text"
                        value={form.state}
                        onChange={(e) => updateForm('state', e.target.value)}
                        placeholder="e.g., Ohio, New York, Texas"
                        className="form-field w-full px-4 py-3.5 text-[13px] rounded-sm placeholder:text-marble/20"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold tracking-widest uppercase text-marble/40 mb-2">
                        Preferred Callback Window
                      </label>
                      <select
                        value={form.callbackWindow}
                        onChange={(e) => updateForm('callbackWindow', e.target.value)}
                        className="form-field w-full px-4 py-3.5 text-[13px] rounded-sm"
                      >
                        <option value="">Select window...</option>
                        {callbackWindows.map((w) => (
                          <option key={w} value={w}>{w}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="btn-gold-outline px-6 py-3.5 text-[11px] font-semibold tracking-widest uppercase rounded-sm"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => form.institutionSize && setStep(3)}
                      disabled={!form.institutionSize}
                      className="btn-red px-8 py-3.5 text-[11px] font-semibold tracking-widest uppercase rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <div>
                  <h3 className="font-serif text-marble text-xl mb-2">How should we reach you?</h3>
                  <p className="text-[12px] text-marble/40 mb-6">
                    All communications are confidential. We do not share client information.
                  </p>
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[10px] font-semibold tracking-widest uppercase text-marble/40 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => updateForm('name', e.target.value)}
                          placeholder="Margaret Chen"
                          className="form-field w-full px-4 py-3.5 text-[13px] rounded-sm placeholder:text-marble/20"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold tracking-widest uppercase text-marble/40 mb-2">
                          Secure Work Email
                        </label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => updateForm('email', e.target.value)}
                          placeholder="m.chen@firstmidwest.com"
                          className="form-field w-full px-4 py-3.5 text-[13px] rounded-sm placeholder:text-marble/20"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold tracking-widest uppercase text-marble/40 mb-2">
                        Brief Description (Optional)
                      </label>
                      <textarea
                        value={form.message}
                        onChange={(e) => updateForm('message', e.target.value)}
                        placeholder="Provide any context that will help us prepare for the consultation..."
                        rows={4}
                        className="form-field w-full px-4 py-3.5 text-[13px] rounded-sm placeholder:text-marble/20 resize-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="btn-gold-outline px-6 py-3.5 text-[11px] font-semibold tracking-widest uppercase rounded-sm"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn-red px-8 py-3.5 text-[11px] font-semibold tracking-widest uppercase rounded-sm"
                    >
                      Submit Confidential Inquiry
                    </button>
                  </div>
                  <p className="text-[10px] text-marble/25 mt-4 leading-relaxed">
                    Attorney advertising. Prior results do not guarantee similar outcomes. Statute LLP. All rights reserved.
                  </p>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}